/**
 * Helpers for Promotion Service checkout APIs (available / preview / commit).
 */
export function formatPromotionDateTime(value: unknown): string {
  if (!value) {
    return new Date().toISOString();
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value);
}

export function extractPromotionCouponCodes(
  appliedCoupons: Array<{ couponCode?: string }> | undefined,
): string[] {
  return (appliedCoupons || [])
    .map((c) => (c?.couponCode || '').trim())
    .filter((code) => !!code);
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => String(item ?? '').trim())
    .filter(Boolean);
}

function readNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => Number(item))
    .filter((id) => !Number.isNaN(id));
}

function readTotalDiscount(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? Math.round(num) : null;
}

/** Order detail has coupon codes or selected promotion offers (UI-only parsing). */
export function hasOrderDetailPromotionSelection(
  orderData: unknown,
): boolean {
  const root = asRecord(orderData);
  if (!root) {
    return false;
  }

  const payment =
    asRecord(root['paymentInfoVO']) ?? asRecord(root['payment_info_vo']);
  if (!payment) {
    return false;
  }

  const couponCodes = readStringArray(
    payment['couponCodes'] ?? payment['coupon_codes'],
  );
  const offerIds = readNumberArray(
    payment['selectedOfferIds'] ?? payment['selected_offer_ids'],
  );
  return couponCodes.length > 0 || offerIds.length > 0;
}

/** Total discount from order detail response; 0 when missing or no promotion/coupon. */
export function resolveOrderDetailTotalDiscount(orderData: unknown): number {
  if (!hasOrderDetailPromotionSelection(orderData)) {
    return 0;
  }

  const root = asRecord(orderData);
  if (!root) {
    return 0;
  }

  const amount =
    asRecord(root['amountInfoVO']) ?? asRecord(root['amount_info_vo']);
  const payment =
    asRecord(root['paymentInfoVO']) ?? asRecord(root['payment_info_vo']);

  const raw =
    amount?.['totalDiscount'] ??
    amount?.['total_discount'] ??
    payment?.['totalDiscount'] ??
    payment?.['total_discount'] ??
    root['totalDiscount'] ??
    root['total_discount'];

  return readTotalDiscount(raw) ?? 0;
}
